'use client';

import { useState, useMemo, useEffect } from 'react';
import { message, Table, Spin, Tabs } from 'antd';
import styles from './LeaderboardModal.module.scss';
import { useLang } from '@/i18n';
import getLocal from '@/app/utils/getLocal';
import EfModal from '@/components/UI/Modal';
import NextImage from '@/components/NextImage';
import { useReferralStore } from '@/components/Referral/ReferralProvider';
import { getCurrentPlanTop } from '@/service/referral';
import { PlanType } from '@/components/Referral/index.d';
import type { ICurrentPlanTop } from '@/components/Referral/index.d';
import { Champion, RunnerUp, SecondRunnerUp } from '@/components/UI/svg/Referral';
import { useGlobal } from '@/utils/useGlobal';

const { TabPane } = Tabs;

interface LeaderboardModalProps {
    visible: boolean;
    onClose: () => void;
}

export default function LeaderboardModal({ visible, onClose }: LeaderboardModalProps) {
    const { t } = useLang('referral.rankinglist');
    const { locale } = getLocal();
    const { isMobile } = useGlobal();
    const { referralInfo } = useReferralStore();

    const [leaderboardData, setLeaderboardData] = useState<{
        current: ICurrentPlanTop[];
        last: ICurrentPlanTop[];
    }>({ current: [], last: [] });
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState<'current' | 'last'>('current');

    // 获取排行榜数据
    const fetchLeaderboardData = async () => {
        if (!referralInfo?.planId) return;

        setLoading(true);
        try {
            const [currentRes, lastRes] = await Promise.all([
                getCurrentPlanTop({ size: 20, planId: referralInfo.planId, lastMon: 0 }, locale),
                getCurrentPlanTop({ size: 20, planId: referralInfo.planId, lastMon: 1 }, locale),
            ]);

            if (currentRes.code !== '0') {
                message.error(currentRes.message);
                return;
            }

            setLeaderboardData({
                current: currentRes.data.content || [],
                last: lastRes.data?.content || [],
            });
        } catch (error) {
            console.error('Failed to fetch leaderboard data:', error);
        } finally {
            setLoading(false);
        }
    };

    // 弹窗打开时获取数据
    useEffect(() => {
        if (visible) {
            fetchLeaderboardData();
        }
    }, [visible, referralInfo?.planId]);

    // 表格列配置
    const columns = useMemo(
        () => [
            {
                title: t('th_ranking'),
                dataIndex: 'ranking',
                key: 'ranking',
                align: 'center' as const,
                width: 150,
                render: (val: number) => {
                    if (val === 1) return <Champion />;
                    if (val === 2) return <RunnerUp />;
                    if (val === 3) return <SecondRunnerUp />;
                    return val || '--';
                },
            },
            {
                title: t('th_name'),
                dataIndex: 'email',
                key: 'email',
                render: (val: string) => {
                    try {
                        return val?.split('@')[0] || val;
                    } catch {
                        return val;
                    }
                },
            },
            {
                title: t('th_success'),
                dataIndex: 'registerCount',
                key: 'registerCount',
                align: 'center' as const,
            },
        ],
        [t],
    );

    // 空数据组件
    const NoData = () => (
        <div className="no-content">
            <div className="no-content-img">
                <NextImage
                    className="pop_topimg"
                    src={'https://websiteoss.ecoflow.com/figma-uploads/1766719911666-h3zwdk-compressed-1766719909450.png'}
                    alt={t('pop_leaderboard_title')}
                    aria-label={t('pop_leaderboard_title')}
                />
            </div>
            <p className="no-content-text">{t(activeTab === 'last' ? 'no_data_last' : 'no_data_current')}</p>
        </div>
    );

    return (
        <EfModal width={640} visible={visible} open={visible} className={styles.ranking_modal} onCancel={onClose} maskClosable={isMobile} centered={!isMobile}>
            <div className="ranking_top">
                <NextImage
                    className="pop_topimg"
                    src={require(`@publicLocal/referral/${isMobile ? 'h5' : 'pc'}/ranking_pop.png`)}
                    alt={t('pop_title')}
                    aria-label={t('pop_title')}
                />
            </div>
            <h5 className="title">{referralInfo?.planType === PlanType.CAMPAIGN ? t('pop_leaderboard_c_title') : t('pop_leaderboard_title')}</h5>
            <p className="subtitle">{t('pop_leaderboard_desc')}</p>

            {referralInfo?.planType !== PlanType.CAMPAIGN && (
                <Tabs className="leaderboard_tabs" activeKey={activeTab} onChange={(val) => setActiveTab(val as 'current' | 'last')} centered>
                    <TabPane tab={t('btn_last')} key="last" />
                    <TabPane tab={t('btn_current')} key="current" />
                </Tabs>
            )}

            {loading ? (
                <div style={{ textAlign: 'center', paddingTop: '50px' }}>
                    <Spin />
                </div>
            ) : leaderboardData[activeTab].length === 0 ? (
                <NoData />
            ) : (
                <Table
                    sticky
                    scroll={{ y: '55vh' }}
                    className="refer_table"
                    rowClassName={(row, i) => `${i % 2 === 1 ? 'odd' : 'even'} ${row.userId ? 'myself' : ''}`}
                    bordered={false}
                    dataSource={leaderboardData[activeTab]}
                    columns={columns}
                    pagination={false}
                    rowKey={(record) => `${record.email}-${record.ranking}`}
                />
            )}
        </EfModal>
    );
}
